/**
 * Sequential bulk label print queue with progress state.
 * Dispatches one label at a time so the printer doesn't get a flood of jobs,
 * and so the UI can show real "N of M" progress.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useRef, useState } from 'react';
import { useLabelPrinting } from './useLabelPrinting';
var initialProgress = {
    status: 'idle',
    total: 0,
    completed: 0,
    current: null,
    failed: []
};
export var useBulkLabelPrint = function () {
    var printContainerLabel = useLabelPrinting().printContainerLabel;
    var _a = useState(initialProgress), progress = _a[0], setProgress = _a[1];
    var cancelRef = useRef(false);
    var lastItemsRef = useRef([]);
    var runQueue = useCallback(function (items) { return __awaiter(void 0, void 0, void 0, function () {
        var failed, _loop_1, i, state_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    cancelRef.current = false;
                    lastItemsRef.current = items;
                    setProgress({
                        status: 'printing',
                        total: items.length,
                        completed: 0,
                        current: (_a = items[0]) !== null && _a !== void 0 ? _a : null,
                        failed: []
                    });
                    failed = [];
                    _loop_1 = function (i) {
                        var item, err_1;
                        return __generator(this, function (_c) {
                            switch (_c.label) {
                                case 0:
                                    if (cancelRef.current) {
                                        setProgress(function (p) { return (__assign(__assign({}, p), { status: 'cancelled', current: null })); });
                                        return [2 /*return*/, { value: void 0 }];
                                    }
                                    item = items[i];
                                    setProgress(function (p) { return (__assign(__assign({}, p), { current: item })); });
                                    _c.label = 1;
                                case 1:
                                    _c.trys.push([1, 3, , 4]);
                                    return [4 /*yield*/, printContainerLabel(item.containerId)];
                                case 2:
                                    _c.sent();
                                    return [3 /*break*/, 4];
                                case 3:
                                    err_1 = _c.sent();
                                    console.error('Bulk print dispatch failed for', item, err_1);
                                    failed.push(item);
                                    return [3 /*break*/, 4];
                                case 4:
                                    setProgress(function (p) { return (__assign(__assign({}, p), { completed: i + 1, failed: failed.slice() })); });
                                    return [2 /*return*/];
                            }
                        });
                    };
                    i = 0;
                    _b.label = 1;
                case 1:
                    if (!(i < items.length)) return [3 /*break*/, 4];
                    return [5 /*yield**/, _loop_1(i)];
                case 2:
                    state_1 = _b.sent();
                    if (typeof state_1 === "object")
                        return [2 /*return*/, state_1.value];
                    _b.label = 3;
                case 3:
                    i++;
                    return [3 /*break*/, 1];
                case 4:
                    setProgress(function (p) { return (__assign(__assign({}, p), { status: failed.length > 0 ? 'error' : 'done', current: null, failed: failed })); });
                    return [2 /*return*/];
            }
        });
    }); }, [printContainerLabel]);
    var start = useCallback(function (items) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (items.length === 0)
                        return [2 /*return*/];
                    return [4 /*yield*/, runQueue(items)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [runQueue]);
    var retryFailed = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var toRetry;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    toRetry = progress.failed.slice();
                    if (toRetry.length === 0)
                        return [2 /*return*/];
                    return [4 /*yield*/, runQueue(toRetry)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [progress.failed, runQueue]);
    var cancel = useCallback(function () {
        cancelRef.current = true;
    }, []);
    var reset = useCallback(function () {
        cancelRef.current = false;
        lastItemsRef.current = [];
        setProgress(initialProgress);
    }, []);
    return { progress: progress, start: start, retryFailed: retryFailed, cancel: cancel, reset: reset };
};
export default useBulkLabelPrint;
